import useActiveAd from '../../hooks/useActiveAd';
import ProductCard from './ProductCard';
import BrandCard from './BrandCard';

/**
 * PartnerBlock — contenu sponsorisé en homepage (4 produits + 1 carte marque).
 *
 * Contenu chargé via GET /api/ads/active/partner_block. Si aucun bloc actif,
 * le composant retourne null (la section disparaît complètement).
 *
 * Layout responsive :
 *   - mobile < 640px : 1 col (tout empilé)
 *   - tablet 640-1023 : 2 cols, carte marque full-width en dernier
 *   - laptop 1024-1279 : 3 cols
 *   - desktop ≥ 1280 : 5 cols (4 produits + marque alignés)
 *
 * Accessibilité :
 *   - section avec aria-label « Contenu sponsorisé »
 *   - label PARTENAIRE en bg foreground / text background (WCAG AA)
 *   - rel="sponsored nofollow noopener" sur tous les liens (sous-composants)
 *   - animations désactivées si prefers-reduced-motion
 */
export default function PartnerBlock() {
  const { ad } = useActiveAd('partner_block');

  // Pas de pub active ou payload incomplet → on masque la section.
  if (!ad) return null;
  if (!Array.isArray(ad.products) || ad.products.length === 0) return null;
  if (!ad.brand || !ad.brand.click_url) return null;

  const label = ad.label_text || 'PARTENAIRE';
  const secondary = ad.secondary_label || 'Sponsorisé';

  return (
    <section
      aria-label="Contenu sponsorisé"
      className="py-10 md:py-12 border-t border-border/50"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-5 md:mb-6">
          <span className="inline-flex items-center px-2.5 py-1 rounded text-[11px] font-bold tracking-[0.12em] bg-foreground text-background">
            {label}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {secondary}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
          {ad.products.slice(0, 4).map((p) => (
            <ProductCard key={p.id} adId={ad.id} product={p} />
          ))}
          <div className="sm:col-span-2 lg:col-span-3 xl:col-span-1">
            <BrandCard adId={ad.id} brand={ad.brand} />
          </div>
        </div>
      </div>
    </section>
  );
}
